import type { FormSubmitResolutionAction } from '@onshape/www-inertia';
import QueryUtil from '../../../../../QueryUtil';
import UnifiedSignupIntent from './UnifiedSignupIntent';
import UnifiedSignupPersona from './UnifiedSignupPersona';
import type UnifiedSignupStartScreen from './UnifiedSignupStartScreen';
import UnifiedSignupEmailType from './UnifiedSignupEmailType';
import UnifiedSignupPlan from './UnifiedSignupPlan';
import type { UnifiedSignupScreenName } from '../UnifiedSignupScreenName';
import UnifiedSignupGradeLevel from './UnifiedSignupGradeLevel';
import type { HekateSubmitFormLiteResult } from '../../../../../../model/HekateSubmitFormLiteResult';
import type { EmailStatus } from '../UnifiedSignupAgent';
import UnifiedSignupPersonaDetails from './UnifiedSignupPersonaDetails';

type FormSubmitStatus = 'submitting' | 'submitted' | 'submit-failed';

type EmailCheckCode = 401 | 200 | 204 | 422 | 500;

/**
 * What information we know about about the current user, via what page they
 * landed on and their answers to questions
 */
export default class UnifiedSignupSessionInfo {
  public startScreen: UnifiedSignupStartScreen;

  public emailType: UnifiedSignupEmailType | null;

  public emailCheckCode: EmailCheckCode | null;

  public emailCheckErrorReason: 'geo-blocked' | string | null;

  /** True if the user's email does not exclude them from joining disco, e.g. company already has a plan */
  public isDiscoEligible: boolean | null;

  public intent: UnifiedSignupIntent | null;

  public persona: UnifiedSignupPersona | null;

  public personaDetails: UnifiedSignupPersonaDetails | null;

  public gradeLevel: UnifiedSignupGradeLevel | null;

  public chosenPlan: UnifiedSignupPlan | null;

  public completedDetailsScreens: Array<UnifiedSignupScreenName | null>;

  public isChooseOwnPlan: boolean;

  public submitFormResult: HekateSubmitFormLiteResult | null;

  // set to true if this form has completely errored out
  public isErroredOut: boolean;

  public isEduRenew: boolean;

  public isProUpgrade: boolean;

  /**
   * For the activation (typically last screen) IF the user has activated there
   * account or if we can't send email, etc..
   */
  public emailStatus: EmailStatus | null;

  public formSubmitStatus: FormSubmitStatus | null;

  public resolutionActions: Array<FormSubmitResolutionAction> | null;

  // Added to move from homepage signup form screen to intent screen.
  public firstName: string | null;

  public isUnderThirteen: boolean | null;

  constructor({ startScreen }: { startScreen: UnifiedSignupStartScreen }) {
    this.startScreen = startScreen;

    this.intent = QueryUtil.buildOrDefault<UnifiedSignupIntent | null>({
      defaultVal: null,
      queryParam: 'dbg-intent',
      builder: (str) => UnifiedSignupIntent.fromString(str),
    });

    this.persona = QueryUtil.buildOrDefault<UnifiedSignupPersona | null>({
      defaultVal: null,
      queryParam: 'dbg-persona',
      builder: (str) => UnifiedSignupPersona.fromString(str),
    });

    this.personaDetails = QueryUtil.buildOrDefault<UnifiedSignupPersonaDetails | null>({
      defaultVal: null,
      queryParam: 'dbg-persona-details',
      builder: (str) => UnifiedSignupPersonaDetails.fromString(str),
    });

    this.gradeLevel = QueryUtil.buildOrDefault({
      defaultVal: null,
      queryParam: 'dbg-grade-level',
      builder: (str) => UnifiedSignupGradeLevel.fromString(str),
    });

    this.emailType = QueryUtil.buildOrDefault({
      defaultVal: null,
      queryParam: 'dbg-email-type',
      builder: (str) => UnifiedSignupEmailType.fromString(str),
    });

    this.emailCheckCode = QueryUtil.buildOrDefault<EmailCheckCode | null>({
      defaultVal: null,
      queryParam: 'dbg-email-check-code',
      builder: (str) => parseInt(str, 10) as EmailCheckCode,
    });

    this.emailCheckErrorReason = QueryUtil.buildOrDefault<'geo-blocked' | string | null>({
      defaultVal: null,
      queryParam: 'dbg-email-check-error-reason',
      builder: (str) => str as 'geo-blocked' | string,
    });

    this.isDiscoEligible = QueryUtil.boolOrDefault<boolean | null>({
      defaultVal: null,
      queryParam: 'dbg-is-disco-eligible',
    });

    this.emailStatus = QueryUtil.identityOrDefault<EmailStatus, null>({
      defaultVal: null,
      queryParam: 'dbg-email-status',
    });

    this.chosenPlan = QueryUtil.buildOrDefault<UnifiedSignupPlan | null>({
      defaultVal: null,
      queryParam: 'dbg-chosen-plan',
      builder: (str) => UnifiedSignupPlan.fromString(str),
    });

    this.completedDetailsScreens = QueryUtil.buildArrayOrDefault<UnifiedSignupScreenName>({
      defaultVal: [],
      queryParam: 'dbg-screens',
      builder: (str: string) => str as UnifiedSignupScreenName,
    });

    this.isChooseOwnPlan = QueryUtil.boolOrDefault({
      defaultVal: false,
      queryParam: 'dbg-choose-own',
    });

    this.submitFormResult = QueryUtil.buildFromMappingOrDefault<HekateSubmitFormLiteResult | null>({
      defaultVal: null,
      queryParam: 'dbg-submit-form-result',
      mapping: {
        success: {
          isSuccess: true,
          statusCode: 201,
        },
      },
    });

    this.formSubmitStatus = QueryUtil.identityOrDefault<FormSubmitStatus, null>({
      defaultVal: null,
      queryParam: 'dbg-form-submit-status',
    });

    this.formSubmitStatus = QueryUtil.identityOrDefault<FormSubmitStatus, null>({
      defaultVal: null,
      queryParam: 'dbg-form-submit-status',
    });

    this.isErroredOut = QueryUtil.boolOrDefault({
      defaultVal: false,
      queryParam: 'dbg-errored-out',
    });

    this.isEduRenew = QueryUtil.boolOrDefault({
      defaultVal: false,
      queryParam: 'dbg-is-edu-renew',
    });

    this.isProUpgrade = QueryUtil.boolOrDefault({
      defaultVal: false,
      queryParam: 'dbg-is-pro-upgrade',
    });

    this.resolutionActions =
      QueryUtil.buildFromMappingOrDefault<Array<FormSubmitResolutionAction> | null>({
        defaultVal: null,
        queryParam: 'dbg-resolution-actions',
        mapping: {
          youtube: [
            {
              procedure: 'subscribeToYoutube',
              params: {},
            },
          ],
          internet: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: 'internet' },
            },
          ],
          web: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: 'the web' },
            },
          ],
          net: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: 'the net' },
            },
          ],
          online: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: 'online' },
            },
          ],
          google: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: 'Google' },
            },
          ],
          yes: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: '"yes"' },
            },
          ],
          idk: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: '"idk"' },
            },
          ],
          a: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: '"a"' },
            },
          ],
          no: [
            {
              procedure: 'tellMeMoreAboutIt',
              params: { replacement: '"no"' },
            },
          ],
        },
      });

    this.firstName = QueryUtil.stringOrDefault<string | null>({
      defaultVal: null,
      queryParam: 'dbg-persona-firstName',
    });

    this.isUnderThirteen = QueryUtil.boolOrDefault({
      defaultVal: false,
      queryParam: 'dbg-is-under-thirteen',
    });
    QueryUtil.logDebug();
  }
}
